import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EtapasBeneficioComponent } from '../../shared/components/etapas-beneficio/etapas-beneficio.component';
import { SessionService } from '../../core/services/session.service';
import { DadosMilitaresService } from '../../core/services/dados-militares.service';
import { RegraService } from '../../core/services/regra.service';
import { AnaliseRegrasResponse, RegraAnalise } from '../../core/models/regra.model';

@Component({
  selector: 'app-regras',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule,
    EtapasBeneficioComponent
  ],
  templateUrl: './regras.component.html',
  styleUrl: './regras.component.scss'
})
export class RegrasComponent implements OnInit {
  beneficiario: any;
  dadosMilitares: any = {};
  loading = false;
  analiseData: AnaliseRegrasResponse | null = null;
  displayedColumns: string[] = ['descricao', 'atende', 'selecionada', 'acoes'];
  exibirApenasQueAtendem = false;
  dataAposentadoria = '';
  regraSelecionadaId: number | null = null;

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private regraService: RegraService,
    private dadosService: DadosMilitaresService,
    private snackBar: MatSnackBar
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.beneficiario = navigation?.extras.state?.['data'];
  }

  ngOnInit(): void {
    this.loadBeneficiarioFromSession();

    if (!this.beneficiario) {
      this.showError('Beneficiário não encontrado. Retornando à pesquisa...');
      setTimeout(() => this.router.navigateByUrl('/pesquisar-militar'), 2000);
      return;
    }

    this.recarregarDadosMilitares();
  }

  /** Busca o contexto militar salvo (com a versão atual) e recalcula a análise. */
  private recarregarDadosMilitares(): void {
    this.dadosService.buscar(this.beneficiario.matricula,this.beneficiario.orgao?.id || this.beneficiario.idOrgao).subscribe({next:d=>{this.dadosMilitares=d ?? {};this.carregarAnaliseRegras();},error:()=>this.carregarAnaliseRegras()});
  }

  private loadBeneficiarioFromSession(): void {
    if (!this.beneficiario) {
      const sessionBeneficiario = this.sessionService.beneficiario;
      if (sessionBeneficiario) {
        this.beneficiario = sessionBeneficiario;
      }
    } else {
      this.sessionService.beneficiario = this.beneficiario;
    }
  }

  carregarAnaliseRegras(): void {
    this.loading = true;

    // Usa a data informada pelo usuário ou a data local de hoje como padrão
    if (!this.dataAposentadoria) {
      this.dataAposentadoria = dataLocalIso(new Date());
    }

    // Pegar o ID do órgão corretamente
    const idOrgao = this.beneficiario.orgao?.id || this.beneficiario.idOrgao;

    this.regraService.analisarRegras({
      matricula: this.beneficiario.matricula,
      idOrgao: idOrgao,
      dataAposentadoria: this.dataAposentadoria
    }).subscribe({
      next: (response) => {
        this.analiseData = response;
        this.atualizarRegraSelecionadaFromResponse();
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar análise de regras:', error);
        this.showError('Erro ao carregar análise de regras');
        this.loading = false;
      }
    });
  }

  salvarDadosMilitares(): void {
    // O militar-back devolve {message} (400/409), {mensagem, codigo} (422/500) ou {detail} (erros do Spring).
    this.dadosService.salvar(this.beneficiario.matricula,this.beneficiario.orgao?.id || this.beneficiario.idOrgao,this.dadosMilitares).subscribe({next:d=>{this.dadosMilitares=d;this.showSuccess('Dados militares salvos.');this.carregarAnaliseRegras();},error:err=>this.showError(err.error?.message ?? err.error?.mensagem ?? err.error?.detail ?? 'Falha ao salvar os dados militares.')});
  }

  private atualizarRegraSelecionadaFromResponse(): void {
    if (this.analiseData) {
      const regraSelecionada = this.analiseData.regras.find(r => r.regraSelecionada);
      this.regraSelecionadaId = regraSelecionada ? regraSelecionada.id : null;
    }
  }

  getRegrasExibidas(): RegraAnalise[] {
    if (!this.analiseData) return [];

    if (this.exibirApenasQueAtendem) {
      return this.analiseData.regras.filter(r => r.atende === true);
    }

    return this.analiseData.regras;
  }

  toggleExibirApenas(): void {
    this.exibirApenasQueAtendem = !this.exibirApenasQueAtendem;
  }

  toggleSelecao(regra: RegraAnalise): void {
    // Só permite selecionar regras que atendem
    if (!regra.atende) {
      this.showError('Apenas regras que atendem podem ser selecionadas');
      return;
    }

    // Se já está selecionada, desseleciona
    if (regra.regraSelecionada) {
      regra.regraSelecionada = false;
      this.regraSelecionadaId = null;
      return;
    }

    // Desseleciona todas as outras regras
    if (this.analiseData) {
      this.analiseData.regras.forEach(r => r.regraSelecionada = false);
    }

    // Seleciona a regra clicada
    regra.regraSelecionada = true;
    this.regraSelecionadaId = regra.id;

    this.salvarRegraSelecionada(regra);
  }

  private salvarRegraSelecionada(regra: RegraAnalise): void {
    const idOrgao = this.beneficiario.orgao?.id || this.beneficiario.idOrgao;

    this.loading = true;

    this.regraService.selecionarRegra({
      matricula: this.beneficiario.matricula,
      idOrgao: idOrgao,
      dataAposentadoria: this.dataAposentadoria,
      idRegraSelecionada: regra.id
    }).subscribe({
      next: () => {
        this.showSuccess('Regra selecionada com sucesso!');
        // A seleção grava o contexto militar e incrementa a versão no servidor; sem recarregar,
        // o próximo "Salvar dados e recalcular" levaria 409.
        this.recarregarDadosMilitares();
      },
      error: (error) => {
        console.error('Erro ao selecionar regra:', error);
        regra.regraSelecionada = false;
        this.regraSelecionadaId = null;
        this.loading = false;
        this.showError('Erro ao selecionar regra');
      }
    });
  }

  downloadQTC(regra: RegraAnalise): void {
    const idOrgao = this.beneficiario.orgao?.id || this.beneficiario.idOrgao;

    this.showInfo('Gerando relatório QTC...');

    this.regraService.downloadQTC({
      matricula: this.beneficiario.matricula,
      orgaoId: idOrgao,
      idRegra: regra.id,
      dataAposentadoria: this.dataAposentadoria
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `QTC_${this.beneficiario.matricula}_${regra.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.showSuccess('Relatório QTC baixado com sucesso!');
      },
      error: (error) => {
        console.error('Erro ao baixar QTC:', error);
        this.showError('Erro ao gerar relatório QTC');
      }
    });
  }

  downloadQRB(regra: RegraAnalise): void {
    const idOrgao = this.beneficiario.orgao?.id || this.beneficiario.idOrgao;

    this.showInfo('Gerando relatório QRB...');

    this.regraService.downloadQRB({
      matricula: this.beneficiario.matricula,
      orgaoId: idOrgao,
      idRegra: regra.id,
      dataAposentadoria: this.dataAposentadoria
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `QRB_${this.beneficiario.matricula}_${regra.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.showSuccess('Relatório QRB baixado com sucesso!');
      },
      error: (error) => {
        console.error('Erro ao baixar QRB:', error);
        this.showError('Erro ao gerar relatório QRB');
      }
    });
  }

  voltarParaHome(): void {
    this.router.navigateByUrl('/pesquisar-militar');
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Fechar', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['error-snackbar']
    });
  }

  private showInfo(message: string): void {
    this.snackBar.open(message, 'OK', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Fechar', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar']
    });
  }
}

/** AAAA-MM-DD no fuso do navegador. toISOString() usa UTC e, depois das 21h em Fortaleza, já devolve o dia seguinte. */
function dataLocalIso(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}
